import { useMemo, useRef } from "react";
import { useParams } from "react-router";
import { useI18n } from "~/i18n";
import { resolveIpAssets } from "~/theme/resolve-theme";
import { SectionHeading } from "~/components/sections/section-heading";
import { useRevealOnScroll } from "~/hooks/use-reveal-on-scroll";
import type { Locale } from "@laftel-ip-originals/notion/locale";

// 캐릭터 이름 배지 — characterId로 캐릭터 섹션 포커스 연동
const CHAR_BADGES: Record<string, { name: string; color: string; characterId: number }[]> = {
  ko: [
    { name: "라온", color: "sky", characterId: 1 },
    { name: "서우", color: "ochre", characterId: 2 },
    { name: "제우", color: "ochre", characterId: 2 },
  ],
  ja: [
    { name: "ラオン", color: "sky", characterId: 1 },
    { name: "ソウ", color: "ochre", characterId: 2 },
    { name: "ジェウ", color: "ochre", characterId: 2 },
  ],
  en: [
    { name: "Laon", color: "sky", characterId: 1 },
    { name: "Seo-u", color: "ochre", characterId: 2 },
    { name: "Jeu", color: "ochre", characterId: 2 },
  ],
};

const BADGE_STYLES: Record<string, string> = {
  sky: "bg-[rgba(var(--ip-primary-glow),0.1)] text-[var(--ip-primary-hover)] border border-[rgba(var(--ip-primary-glow),0.3)]",
  ochre: "bg-[rgba(190,130,60,0.08)] text-[#a06820] border border-[rgba(190,130,60,0.28)]",
};

function renderWithBadges(
  text: string,
  locale: string,
  seen: Set<string>,
  onBadgeClick?: (characterId: number) => void,
) {
  const badges = CHAR_BADGES[locale] ?? [];
  if (!badges.length) return text;
  const pattern = badges.map((b) => b.name.replace(/[-]/g, "\\$&")).join("|");
  const regex = new RegExp(`(${pattern})`, "g");
  const parts = text.split(regex);
  return parts.map((part, i) => {
    const badge = badges.find((b) => b.name === part);
    if (badge && !seen.has(part)) {
      seen.add(part);
      return (
        <span
          key={i}
          className={`char-badge char-badge--${badge.color} char-badge--link inline-flex items-center px-[7px] rounded-[20px] text-[0.82em] font-normal align-middle mx-0.5 leading-[1.6] cursor-pointer transition-opacity hover:opacity-70 ${BADGE_STYLES[badge.color]}`}
          onClick={() => onBadgeClick?.(badge.characterId)}
        >
          {part} ›
        </span>
      );
    }
    return part;
  });
}

interface SynopsisProps {
  heading: Record<Locale, string>;
  text: Record<Locale, string>;
  onBadgeClick?: (characterId: number) => void;
}

export function Synopsis({ heading, text, onBadgeClick }: SynopsisProps) {
  const { locale } = useI18n();
  const { slug = "" } = useParams<{ slug: string }>();
  const assets = resolveIpAssets(slug);
  const bodyRef = useRef<HTMLDivElement>(null);

  const polaroids = useMemo(
    () => [
      {
        src: `${assets.imagesBase}/introduction_01.webp`,
        rotate: -6,
        side: "left" as const,
        top: "8%",
      },
      {
        src: `${assets.imagesBase}/introduction_02.webp`,
        rotate: 4,
        side: "right" as const,
        top: "20%",
      },
      {
        src: `${assets.imagesBase}/introduction_03.webp`,
        rotate: -3,
        side: "left" as const,
        top: "52%",
      },
    ],
    [assets.imagesBase],
  );

  const paragraphs = (text[locale] ?? "").split("\n\n");
  const badgeSeen = useRef(new Set<string>());
  badgeSeen.current = new Set<string>();

  useRevealOnScroll(bodyRef, { selector: ".reveal-slide", resetKey: locale });

  return (
    <section
      id="introduction"
      className="relative z-[2] pt-[200px] pb-[320px] max-md:pt-[80px] max-md:pb-[120px] bg-white bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(255,255,255,0.7)_100%),linear-gradient(rgba(var(--ip-primary-glow),0.12)_1px,transparent_1px),linear-gradient(90deg,rgba(var(--ip-primary-glow),0.12)_1px,transparent_1px)] bg-[length:100%_100%,28px_28px,28px_28px] after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[120px] after:bg-gradient-to-b after:from-transparent after:to-white after:pointer-events-none"
    >
      {/* Desktop polaroids */}
      <div className="contents max-md:hidden">
        {polaroids.map((p, i) => (
          <div
            key={i}
            className={`synopsis__polaroid absolute w-[200px] bg-white p-3 pb-10 shadow-[0_16px_20px_-8px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.05)] z-0 transition-all duration-300 hover:z-10 hover:scale-[1.1] hover:shadow-[0_20px_32px_-8px_rgba(0,0,0,0.15),0_2px_6px_rgba(0,0,0,0.06)] ${
              p.side === "left"
                ? "synopsis__polaroid--left left-[calc(50%-380px-200px)]"
                : "synopsis__polaroid--right right-[calc(50%-380px-200px)]"
            }`}
            style={{
              ["--pol-rotate" as string]: `${p.rotate}deg`,
              transform: "rotate(var(--pol-rotate))",
              top: p.top,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.setProperty("--pol-rotate", "0deg");
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.setProperty("--pol-rotate", `${p.rotate}deg`);
            }}
          >
            <img
              src={p.src}
              alt={`polaroid ${i + 1}`}
              className="w-full aspect-[3/4] object-cover block"
            />
          </div>
        ))}
      </div>

      <div className="max-w-full mx-auto px-[clamp(24px,5vw,120px)]">
        <SectionHeading
          imgSrc={`${assets.imagesBase}/title_introduction.webp`}
          alt="Introduction"
          raised
        />

        <div ref={bodyRef} className="synopsis__body relative z-[1] mx-auto max-w-[680px]">
          <h3 className="synopsis__heading reveal-slide mb-5 text-center text-[18px] font-semibold leading-[1.7] tracking-[0.05em] text-[var(--ip-text)]">
            {heading[locale]}
          </h3>
          <div className="synopsis__para reveal-slide">
            {paragraphs.map((p, i) => (
              <p
                key={i}
                className="mb-6 whitespace-pre-line text-center text-[15px] font-normal leading-[2.1] tracking-[0.04em] text-[var(--ip-text)] last:mb-0 max-md:text-sm"
              >
                {renderWithBadges(p, locale, badgeSeen.current, onBadgeClick)}
              </p>
            ))}
          </div>
        </div>

        {/* Mobile polaroids */}
        <div className="hidden max-md:flex justify-center gap-4 mt-12 flex-wrap">
          {polaroids.map((p, i) => (
            <div
              key={i}
              className="synopsis__polaroid w-[140px] bg-white p-3 pb-10 shadow-[0_16px_20px_-8px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.05)]"
              style={{ transform: `rotate(${p.rotate}deg)` }}
            >
              <img
                src={p.src}
                alt={`polaroid ${i + 1}`}
                className="w-full aspect-[3/4] object-cover block"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
